import { type ReactNode } from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { AccountSessionValidator } from '../../features/account/routing/AccountSessionValidator.js';
import { RequireCustomerAuth } from '../../features/account/routing/RequireCustomerAuth.js';
import {
  EntitlementRouteGuard,
} from '../../features/entitlements/customerEntitlementGuards.js';
import { FeatureFlagRouteGuard } from '../../features/entitlements/FeatureFlagRouteGuard.js';
import { AccountRecurringSetupPage } from '../../routing/lazyRoutes.wave2.js';
import { DonateLegacyPathRedirect } from '../../features/donation/DonateLegacyPathRedirect.js';
import { RemountBoundary } from '../../shared/components/ErrorBoundary/RemountBoundary.js';
import { RouteErrorFallback } from '../../shared/components/ErrorBoundary/RouteErrorFallback.js';
import {
  AccountAndPrivacyPage,
  AccountDonationsPage,
  AccountHomePage,
  AccountOrdersPage,
  AfterSaleIntakePage,
  DurableConfirmationPage,
  AccountPaymentsPage,
  AccountPaymentClaimPage,
  AccountPaymentQrPage,
  AccountShell,
  AccountShopKiosksPage,
  AccountShopTenantPage,
} from './lazyRouteComponents.js';
import { withRegisteredScreen } from './registryGuard.js';
import {
  CompleteVerificationRouteElement,
  Wave1Suspense,
} from './routeHelpers.js';

/**
 * Wave C — hard redirect `/{tenant}/account/*` → `/account/*` (suffix + query + hash).
 * Ban: never leave callers on `/{tenant}/account/add-phone` as a final destination —
 * always normalize to platform `/account/add-phone`.
 */
export function TenantAccountLegacyRedirect(): JSX.Element {
  const location = useLocation();
  const params = useParams();
  const rest = params['*'];
  const suffix = rest !== undefined && rest.length > 0 ? `/${rest}` : '';
  return (
    <Navigate
      to={`/account${suffix}${location.search}${location.hash}`}
      replace
    />
  );
}

/** Tenant-scoped legacy account URLs — redirect only (platform tree owns the hub). */
export function TenantAccountLegacyRedirectRoutes(): JSX.Element {
  return <Route path="account/*" element={<TenantAccountLegacyRedirect />} />;
}

/** L3 account-cluster boundary — single wrap site in this module (not AccountShell Outlet). */
function withAccountBoundary(node: ReactNode): JSX.Element {
  return (
    <RemountBoundary
      feature="account"
      fallback={({ onRetry, feature }) => (
        <RouteErrorFallback
          onRetry={onRetry}
          feature={feature}
          testId="customer-eb-l3-account"
        />
      )}
    >
      {node}
    </RemountBoundary>
  );
}

/** Account hub route tree — extracted from retail (GC-P1-07). */
export function TenantAccountRoutes(): JSX.Element {
  return (
    <Route
      path="account"
      element={
        /*
          Auth gate only at the account shell — do NOT wrap the whole tree in
          customer_auth_pwa EntitlementRouteGuard (that blanks Objednávky / Nákupy
          with SurfaceUnavailable). Narrow that entitlement to privacy/hub children.
        */
        <RequireCustomerAuth>
          <AccountSessionValidator>
            <Wave1Suspense>
              {withAccountBoundary(<AccountShell />)}
            </Wave1Suspense>
          </AccountSessionValidator>
        </RequireCustomerAuth>
      }
    >
      <Route
        index
        element={withRegisteredScreen(
          'MS-HUB-025',
          <EntitlementRouteGuard blockKeys={['customer_auth_pwa']}>
            <AccountHomePage />
          </EntitlementRouteGuard>,
        )}
      />
      <Route path="home" element={<Navigate to=".." replace />} />
      {/* Legacy bottom-nav promo tab → account home (kupóny + promo events). */}
      <Route path="promo" element={<Navigate to=".." replace />} />
      <Route
        path="donations"
        element={withRegisteredScreen(
          'MS-ACC-061',
          <EntitlementRouteGuard blockKeys={['donation']}>
            <FeatureFlagRouteGuard requires={{ master: true, anyOf: ['my-donations'] }}>
              <AccountDonationsPage />
            </FeatureFlagRouteGuard>
          </EntitlementRouteGuard>,
        )}
      />
      <Route
        path="recurring-donations"
        element={withRegisteredScreen(
          'MS-ACC-062',
          <EntitlementRouteGuard blockKeys={['donation']}>
            <FeatureFlagRouteGuard requires={{ master: true, anyOf: ['recurring'] }}>
              <Wave1Suspense>
                <AccountRecurringSetupPage />
              </Wave1Suspense>
            </FeatureFlagRouteGuard>
          </EntitlementRouteGuard>,
        )}
      />
      {/*
        Nákupy / Objednávky tab destinations must always mount — never blank the
        whole tab on customer_auth_pwa / product_vending / surface_customer.
        Unlistable tenants are filtered from Obchody (public directory), not from
        these screens. Sign-in stays via RequireCustomerAuth on the account shell.
      */}
      <Route
        path="orders"
        element={withRegisteredScreen('MS-ORD-050', <AccountOrdersPage />)}
      />
      <Route
        path="orders/:transactionId/confirmation"
        element={withRegisteredScreen('MS-ORD-053', <DurableConfirmationPage />)}
      />
      <Route
        path="orders/:transactionId/after-sale"
        element={
          <Wave1Suspense>
            <AfterSaleIntakePage />
          </Wave1Suspense>
        }
      />
      <Route
        path="orders/:transactionId"
        element={withRegisteredScreen('MS-ORD-051', <AccountOrdersPage />)}
      />
      <Route
        path="shop"
        element={withRegisteredScreen('MS-ACC-063', <AccountShopTenantPage />)}
      />
      <Route
        path="shop/sales-points"
        element={withRegisteredScreen('MS-ACC-060', <AccountShopKiosksPage />)}
      />
      <Route path="shop/pm" element={<Navigate to="sales-points" replace />} />
      <Route
        path="payments"
        element={withRegisteredScreen('MS-HUB-022', <AccountPaymentsPage />)}
      />
      <Route
        path="payments/:paymentId/qr"
        element={withRegisteredScreen('MS-ORD-052', <AccountPaymentQrPage />)}
      />
      <Route
        path="payments/claim"
        element={withRegisteredScreen(
          'MS-ACC-070',
          <Wave1Suspense>
            <AccountPaymentClaimPage />
          </Wave1Suspense>,
        )}
      />
      <Route
        path="account-and-privacy"
        element={withRegisteredScreen(
          'MS-HUB-023',
          <EntitlementRouteGuard blockKeys={['customer_auth_pwa']}>
            <Wave1Suspense>
              <AccountAndPrivacyPage />
            </Wave1Suspense>
          </EntitlementRouteGuard>,
        )}
      />
      {/* ADR-012 — account-scoped donate legacy paths redirect to canonical /donate */}
      <Route path="donate/*" element={<DonateLegacyPathRedirect />} />
      <Route
        path="privacy"
        element={<Navigate to="../account-and-privacy#privacy" replace />}
      />
      <Route
        path="settings"
        element={<Navigate to="../account-and-privacy#credentials" replace />}
      />
      <Route path="complete-verification" element={CompleteVerificationRouteElement()} />
      {/* Wave 4.6 G9 — add-phone mounts as App.tsx sibling of ConfirmEmail (outside RequireCustomerAuth). */}
    </Route>
  );
}
